import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../utils/cn';
import { type SessionLogDetails } from '../../services/api/sessions';

const InfoRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
    <div className="text-xs text-slate-500">{label}</div>
    <div className="mt-1 text-sm font-semibold text-slate-900 break-all">{value}</div>
  </div>
);

interface Props {
  sessionDetails: SessionLogDetails;
}

export const SessionAdvancedDetails = ({ sessionDetails }: Props) => {
  const [open, setOpen] = useState(false);
  const { state, totals } = sessionDetails;

  const hasPreferences = state.userPreferences && Object.keys(state.userPreferences).length > 0;
  const hasMetadata = state.metadata && Object.keys(state.metadata).length > 0;
  const activeProductContext = (state.userPreferences as any)?.activeProductContext;

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-100 transition-colors"
      >
        <span>Avancerade detaljer</span>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-slate-400 transition-transform duration-200',
            open && 'rotate-180',
          )}
        />
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
              Tidsstatistik
            </div>
            <div className="grid grid-cols-2 gap-2">
              <InfoRow label="Anvandarmeddelanden" value={totals.userMessages} />
              <InfoRow label="AI-svar" value={totals.assistantMessages} />
              {totals.averageResponseMs != null && (
                <InfoRow label="Snitt svarstid" value={`${totals.averageResponseMs} ms`} />
              )}
              {totals.maxResponseMs != null && (
                <InfoRow label="Max svarstid" value={`${totals.maxResponseMs} ms`} />
              )}
            </div>
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
              Session
            </div>
            <div className="grid grid-cols-2 gap-2">
              {state.language && <InfoRow label="Detekterat sprak" value={state.language} />}
              {state.createdAt && (
                <InfoRow label="Skapad" value={new Date(state.createdAt).toLocaleString('sv-SE')} />
              )}
              {state.endedAt && (
                <InfoRow label="Avslutad" value={new Date(state.endedAt).toLocaleString('sv-SE')} />
              )}
              {state.expiresAt && (
                <InfoRow label="Utgar" value={new Date(state.expiresAt).toLocaleString('sv-SE')} />
              )}
            </div>
          </div>

          {state.outfitState && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
                Outfit
              </div>

              {activeProductContext && (
                <div className="mb-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                  <span className="font-semibold">Startad fran produktsida:</span>{' '}
                  {typeof activeProductContext === 'object'
                    ? activeProductContext.name ||
                      activeProductContext.productId ||
                      JSON.stringify(activeProductContext)
                    : String(activeProductContext)}
                </div>
              )}

              {state.outfitState.outfitContext?.freeText && (
                <div className="mb-3 rounded-lg border border-slate-200 bg-white px-3 py-2">
                  <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                    Stil / tillfall (freeText)
                  </div>
                  <p className="text-xs italic text-slate-700">
                    &ldquo;{state.outfitState.outfitContext.freeText}&rdquo;
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-2">
                {state.outfitState.phase && <InfoRow label="Fas" value={state.outfitState.phase} />}
                {state.outfitState.occasionLabel && (
                  <InfoRow label="Tillfälle" value={state.outfitState.occasionLabel} />
                )}
                {state.outfitState.totalPrice != null && (
                  <InfoRow
                    label="Budget/totalt"
                    value={`${state.outfitState.totalPrice} ${state.outfitState.currency ?? 'SEK'}`}
                  />
                )}
                {state.outfitState.selections && (
                  <InfoRow
                    label="Valda slots"
                    value={Object.keys(state.outfitState.selections).join(', ') || '\u2013'}
                  />
                )}
              </div>

              {state.outfitState.outfitContext?.selectedArticleGroups?.length ? (
                <div className="mt-3">
                  <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                    Valda artikelgrupper
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {state.outfitState.outfitContext.selectedArticleGroups.map((ag) => (
                      <span
                        key={`${ag.id}-${ag.slot}`}
                        className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-[11px] text-slate-700"
                      >
                        <span className="font-medium">{ag.name}</span>
                        <span className="text-slate-400">&rarr;</span>
                        <span className="text-slate-500">{ag.slot}</span>
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}

              {state.outfitState.outfitContext?.categorySequence?.length ? (
                <div className="mt-3">
                  <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                    Kategoriordning
                  </div>
                  <ol className="flex flex-wrap gap-1.5">
                    {state.outfitState.outfitContext.categorySequence.map((cat, i) => (
                      <li
                        key={`${cat}-${i}`}
                        className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-[11px] text-slate-700"
                      >
                        <span className="text-slate-400">{i + 1}.</span>
                        {cat}
                      </li>
                    ))}
                  </ol>
                </div>
              ) : null}

              {state.outfitState.rejectedProductIds?.length ? (
                <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                  <div className="mb-1 flex items-center gap-2">
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-600">
                      Avvisade produkter
                    </span>
                    <span className="rounded-full bg-amber-200 px-1.5 py-0.5 text-[10px] font-bold text-amber-800">
                      {state.outfitState.rejectedProductIds.length}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {state.outfitState.rejectedProductIds.map((id) => (
                      <span
                        key={id}
                        className="rounded bg-white px-1.5 py-0.5 font-mono text-[10px] text-amber-700 border border-amber-200"
                      >
                        {id}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          )}

          {hasPreferences && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
                Anvandarpreferenser
              </div>
              <pre className="text-xs bg-white border border-slate-200 rounded-lg p-3 overflow-auto max-h-40 text-slate-700">
                {JSON.stringify(state.userPreferences, null, 2)}
              </pre>
            </div>
          )}

          {hasMetadata && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
                Metadata
              </div>
              <pre className="text-xs bg-white border border-slate-200 rounded-lg p-3 overflow-auto max-h-40 text-slate-700">
                {JSON.stringify(state.metadata, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
